// src/components/employee/EmployeePortal.js
import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import EmployeeSidebar from './EmployeeSidebar';
import EmployeeHeader from './EmployeeHeader';
import EmployeeDashboard from './EmployeeDashboard';
import EmployeeProfile from './EmployeeProfile';
import EmployeeAttendance from './EmployeeAttendance';
import EmployeeLeaves from './EmployeeLeaves';
import EmployeePayroll from './EmployeePayroll';
import EmployeeDocuments from './EmployeeDocumentsAPI';
import EmployeeTeam from './EmployeeTeam';
import EmployeeOnboarding from './EmployeeOnboarding';
import ComprehensiveOnboarding from './OnboardingWizardRedesign';
import EmployeeOrgView from './EmployeeOrgView';
import EmployeePerformance from './EmployeePerformance';
import EmployeeGoals from './EmployeeGoals';
import AssignedTeamTasks from './teamTasks/AssignedTeamTasks';
import { useAuth } from '../../hooks/useAuth';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useEmployees } from '../../contexts/EmployeeContext';
import EmployeeProfileViewWrapper from '../common/EmployeeProfileViewWrapper';
import { TaskBox } from '../common/taskbox';
import { VibeFeed } from '../common/vibe';
import SettingsContent from '../common/SettingsContent';
import { isInExitProcess, getDaysRemainingInNoticePeriod } from '../../utils/exitStatusHelper';
import exitService from '../../services/exit.service';

const EmployeePortal = () => {
    const { user, logout } = useAuth();
    const { onboardingData } = useOnboarding();
    const { employees } = useEmployees();
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [exitRequest, setExitRequest] = useState(null);

    // Find the current employee by matching email
    const currentEmployee = employees.find(emp => emp.email === user?.email) || user;
    const employeeId = currentEmployee?.id;

    // Check if user is onboarded (admin approved)
    const isOnboarded = employeeId && onboardingData ? onboardingData[employeeId]?.adminReviewed === true : false;

    // Check if employee is in exit process or has completed exit
    const inExitProcess = isInExitProcess(currentEmployee);
    const hasCompletedExit = currentEmployee?.exit_completed_at || currentEmployee?.employment_status === 'exited';
    const daysRemaining = getDaysRemainingInNoticePeriod(currentEmployee, exitRequest);

    // Fetch exit request details if in exit process OR has completed exit (to show appropriate banner)
    useEffect(() => {
        const fetchExitRequest = async () => {
            // Fetch if employee has any exit-related status
            if (employeeId && (inExitProcess || hasCompletedExit || currentEmployee?.exit_initiated_at)) {
                try {
                    // Use employee-accessible endpoint (not admin endpoint)
                    const response = await exitService.getMyExitRequest();
                    console.log('Exit request API response:', response);
                    if (response.success && response.data) {
                        setExitRequest(response.data);
                    }
                } catch (error) {
                    console.error('Error fetching exit request:', error);
                }
            }
        };
        fetchExitRequest();
    }, [employeeId, inExitProcess, hasCompletedExit, currentEmployee]);

    // Debug log for route protection
    useEffect(() => {
        if (employeeId) {
            // //console.log('🛡️ EmployeePortal - Route Protection Status:', {
            //     employeeId,
            //     userEmail: user?.email,
            //     userName: `${currentEmployee?.first_name || user?.first_name} ${currentEmployee?.last_name || user?.last_name}`,
            //     isOnboarded,
            //     onboardingData: onboardingData?.[employeeId],
            //     adminReviewed: onboardingData?.[employeeId]?.adminReviewed,
            //     willBlockRoutes: !isOnboarded,
            //     timestamp: new Date().toISOString()
            // });
        }
    }, [employeeId, user?.email, currentEmployee, isOnboarded, onboardingData]);

    // Color mapping for Tailwind JIT - must use complete class names
    const getStatusBadgeClasses = (color) => {
        const colorMap = {
            yellow: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200',
            green: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200',
            blue: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200',
            red: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200',
            orange: 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200',
            purple: 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200',
            gray: 'bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200',
            amber: 'bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-200',
        };
        return colorMap[color] || colorMap.gray;
    };

    // Exit Status Banner Component
    const ExitStatusBanner = () => {
        // Show banner if in exit process OR exit is completed
        const isExitCompleted = exitRequest?.status === 'completed' || hasCompletedExit;

        if (!inExitProcess && !isExitCompleted && !exitRequest) return null;

        // FIX: Check both snake_case and camelCase field names from API
        const exitDate = exitRequest?.approved_exit_date || exitRequest?.approvedExitDate ||
                        exitRequest?.requested_exit_date || exitRequest?.requestedExitDate ||
                        exitRequest?.actual_exit_date || exitRequest?.actualExitDate;
        const exitDateFormatted = exitDate ? new Date(exitDate).toLocaleDateString('en-IN', {
            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
        }) : 'Not specified';

        // FIX: Add null check for exitService and provide fallback
        const statusInfo = exitService?.formatExitStatus
            ? exitService.formatExitStatus(exitRequest?.status || 'pending_approval')
            : { label: 'Notice Period', color: 'yellow', icon: '⏳' };
        const statusBadgeClasses = getStatusBadgeClasses(statusInfo?.color || 'yellow');

        // If exit is completed, show a different banner
        if (isExitCompleted) {
            return (
                <div className="mb-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                            <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="text-lg font-semibold text-red-900 dark:text-red-200">
                                    Your Exit Process is Complete
                                </h3>
                                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200">
                                    Exited
                                </span>
                            </div>
                            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                                <div>
                                    <span className="text-red-700 dark:text-red-300 font-medium">Exit Date:</span>
                                    <p className="text-red-900 dark:text-red-100 font-semibold">{exitDateFormatted}</p>
                                </div>
                                {exitRequest?.exit_type && (
                                    <div>
                                        <span className="text-red-700 dark:text-red-300 font-medium">Exit Type:</span>
                                        <p className="text-red-900 dark:text-red-100 font-semibold capitalize">
                                            {exitService?.formatExitType
                                                ? exitService.formatExitType(exitRequest.exit_type)
                                                : exitRequest.exit_type.replace(/_/g, ' ')}
                                        </p>
                                    </div>
                                )}
                            </div>
                            <p className="mt-3 text-sm text-red-700 dark:text-red-300">
                                Your employment has ended. Your access will be disabled shortly. Please contact HR if you need any documents or have questions.
                            </p>
                        </div>
                    </div>
                </div>
            );
        }

        // In progress exit banner
        return (
            <div className="mb-4 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
                <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                        <svg className="w-6 h-6 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-lg font-semibold text-amber-900 dark:text-amber-200">
                                You are in Notice Period
                            </h3>
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusBadgeClasses}`}>
                                {statusInfo.icon} {statusInfo.label}
                            </span>
                        </div>
                        <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                            <div>
                                <span className="text-amber-700 dark:text-amber-300 font-medium">Last Working Day:</span>
                                <p className="text-amber-900 dark:text-amber-100 font-semibold">{exitDateFormatted}</p>
                            </div>
                            {daysRemaining !== null && (
                                <div>
                                    <span className="text-amber-700 dark:text-amber-300 font-medium">Days Remaining:</span>
                                    <p className="text-amber-900 dark:text-amber-100 font-semibold">
                                        {daysRemaining > 0
                                            ? `${daysRemaining} days`
                                            : daysRemaining === 0
                                                ? 'Today is your last day!'
                                                : `Exit date passed ${Math.abs(daysRemaining)} day${Math.abs(daysRemaining) > 1 ? 's' : ''} ago`}
                                    </p>
                                </div>
                            )}
                            {exitRequest?.exit_type && (
                                <div>
                                    <span className="text-amber-700 dark:text-amber-300 font-medium">Exit Type:</span>
                                    <p className="text-amber-900 dark:text-amber-100 font-semibold capitalize">
                                        {exitService?.formatExitType
                                            ? exitService.formatExitType(exitRequest.exit_type)
                                            : exitRequest.exit_type.replace(/_/g, ' ')}
                                    </p>
                                </div>
                            )}
                        </div>
                        <p className="mt-3 text-sm text-amber-700 dark:text-amber-300">
                            Please ensure all your handover tasks are completed and clearance formalities are done before your last working day.
                        </p>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="h-screen flex bg-gray-100 dark:bg-gray-900 overflow-hidden">
            <EmployeeSidebar
                isOpen={sidebarOpen}
                onToggle={() => setSidebarOpen(!sidebarOpen)}
                user={user}
            />

            <div className="flex-1 flex flex-col min-w-0">
                <EmployeeHeader
                    user={user}
                    onLogout={logout}
                />

                <main className="flex-1 p-6 overflow-y-auto">
                    {/* Exit Status Banner - Shows when employee is in notice period */}
                    <ExitStatusBanner />

                    <Routes>
                        <Route path="/" element={<Navigate to="/employee/dashboard" />} />
                        <Route path="/dashboard" element={<EmployeeDashboard />} />
                        <Route path="/profile" element={<EmployeeProfile />} />
                        <Route path="/onboarding" element={<ComprehensiveOnboarding />} />
                        <Route path="/attendance" element={<EmployeeAttendance />} />
                        <Route path="/leaves" element={<EmployeeLeaves />} />
                        <Route path="/payroll" element={<EmployeePayroll />} />
                        <Route path="/documents" element={<EmployeeDocuments />} />
                        <Route path="/team" element={<EmployeeTeam />} />
                        <Route path="/org-view" element={<EmployeeOrgView />} />
                        <Route path="/performance" element={<EmployeePerformance />} />
                        <Route path="/goals" element={<EmployeeGoals employeeId={employeeId} />} />
                        <Route path="/team-tasks" element={<Navigate to="/employee/taskbox?view=team" replace />} />
                        <Route path="/view-profile/:id" element={<EmployeeProfileViewWrapper backPath="/employee/dashboard" />} />
                        <Route path="/taskbox" element={<TaskBox userRole="employee" />} />
                        <Route path="/vibe" element={<VibeFeed />} />
                        <Route path="/settings" element={<SettingsContent />} />
                    </Routes>
                </main>
            </div>
        </div>
    );
};

export default EmployeePortal;
